<template>
  <v-card variant="outlined" class="workflow-run-panel">
    <v-card-title class="d-flex align-center py-2 px-4">
      <v-icon :icon="headerIcon" :color="headerColor" size="small" class="mr-2" />
      <span class="text-subtitle-1">Factory Run</span>
      <v-spacer />
      <v-chip v-if="run.status !== 'idle'" size="small" density="compact" :color="headerColor">
        {{ run.status }}
      </v-chip>
      <span v-if="run.usage" class="text-caption text-medium-emphasis ml-3">
        {{ run.usage.totalTokens || 0 }} total tokens
      </span>
      <span v-if="run.startedAt" class="text-caption text-medium-emphasis ml-3">
        {{ statusTimestampLabel }}
      </span>
      <v-btn
        v-if="run.status === 'running'"
        size="small"
        density="compact"
        variant="tonal"
        color="warning"
        class="ml-3"
        @click="$emit('pause')"
      >
        <v-icon icon="mdi-pause" size="small" class="mr-1" />
        Pause
      </v-btn>
      <v-btn
        v-if="run.status === 'paused'"
        size="small"
        density="compact"
        variant="tonal"
        color="primary"
        class="ml-3"
        @click="$emit('resume')"
      >
        <v-icon icon="mdi-play" size="small" class="mr-1" />
        Resume
      </v-btn>
      <v-btn
        v-if="run.status === 'running' || run.status === 'paused'"
        size="small"
        density="compact"
        variant="tonal"
        color="error"
        class="ml-3"
        @click="$emit('cancel')"
      >
        <v-icon icon="mdi-stop" size="small" class="mr-1" />
        Stop
      </v-btn>
    </v-card-title>
    <v-divider />

    <v-card-text class="pa-3 workflow-run-content">
      <div v-if="run.stepRuns.length > 0" class="mb-3">
        <p class="text-overline mb-2">Steps ({{ run.stepRuns.length }})</p>
        <FactoryStepRunCard
          v-for="stepRun in run.stepRuns"
          :key="stepRun.stepRunId"
          :step-run="stepRun"
          @open-workflow="openWorkflowRun"
          @open-chat="openChatSession"
        />
      </div>

      <div v-if="run.status === 'completed' && run.carryPayload">
        <div class="d-flex align-center justify-space-between mb-2">
          <p class="text-overline mb-0">Final Output</p>
        </div>
        <v-card variant="outlined" class="pa-2">
          <FormattingCard :content="run.carryPayload" :expanded="'true'" :title="null" :streaming="false" />
        </v-card>
      </div>

      <v-alert v-if="run.error" type="error" density="compact" class="mt-3">
        {{ run.error }}
      </v-alert>

      <div v-if="run.status === 'idle'" class="text-center text-medium-emphasis pa-8">
        <v-icon icon="mdi-play-circle-outline" size="48" color="grey" />
        <p class="mt-2">Click "Run Factory" to execute</p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import FormattingCard from '@/components/cards/FormattingCard.vue'
import FactoryStepRunCard from '@/components/factories/FactoryStepRunCard.vue'
import type { FactoryRunDisplay, FactoryStepRunRecord } from '@/types/factories'
import { getDashboardTabPath } from '@/router/dashboardTabs'

const props = defineProps<{
  run: FactoryRunDisplay
}>()

defineEmits<{
  (e: 'pause'): void
  (e: 'resume'): void
  (e: 'cancel'): void
}>()

const store = useStore()
const router = useRouter()

async function openWorkflowRun(stepRun: FactoryStepRunRecord) {
  if (!stepRun.workflowRunId || !stepRun.invoked.workflowConfigId) return
  await store.dispatch('setPendingWorkflowRun', {
    workflowId: stepRun.invoked.workflowConfigId,
    runId: stepRun.workflowRunId
  })
  await store.dispatch('setTab', 'workflows')
  if (router.currentRoute.value.path !== getDashboardTabPath('workflows')) {
    await router.push(getDashboardTabPath('workflows'))
  }
}

async function openChatSession(stepRun: FactoryStepRunRecord) {
  if (!stepRun.chatId) return
  let hydrated = store.getters.getHydratedFactoryStepRun?.(stepRun.stepRunId)
  if (!hydrated) {
    await store.dispatch('fetchHydratedFactoryStepRun', { runId: stepRun.runId, stepRunId: stepRun.stepRunId })
    hydrated = store.getters.getHydratedFactoryStepRun?.(stepRun.stepRunId)
  }
  const chatSession = hydrated?.chatSession
  if (!chatSession) return
  await store.dispatch('setPendingChatAgent', {
    slug: chatSession.agentSlug,
    ownerUsername: chatSession.agentUsername,
    chatId: chatSession.id
  })
  await store.dispatch('setTab', 'chat')
  if (router.currentRoute.value.path !== getDashboardTabPath('chat')) {
    await router.push(getDashboardTabPath('chat'))
  }
}

const headerIcon = computed(() => {
  switch (props.run.status) {
    case 'idle': return 'mdi-circle-outline'
    case 'running': return 'mdi-loading mdi-spin'
    case 'paused': return 'mdi-pause-circle'
    case 'completed': return 'mdi-check-circle'
    case 'cancelled': return 'mdi-cancel'
    case 'error': return 'mdi-alert-circle'
    default: return 'mdi-circle-outline'
  }
})

const headerColor = computed(() => {
  switch (props.run.status) {
    case 'idle': return 'grey'
    case 'running': return 'primary'
    case 'paused': return 'warning'
    case 'completed': return 'success'
    case 'cancelled': return 'warning'
    case 'error': return 'error'
    default: return 'grey'
  }
})

const statusTimestampLabel = computed(() => {
  if (!props.run.startedAt) return ''
  if (props.run.status === 'completed' && props.run.completedAt) {
    return `Completed ${new Date(props.run.completedAt).toLocaleString()}`
  }
  if (props.run.status === 'cancelled' && props.run.cancelledAt) {
    return `Cancelled ${new Date(props.run.cancelledAt).toLocaleString()}`
  }
  if (props.run.status === 'paused' && props.run.pausedAt) {
    return `Paused ${new Date(props.run.pausedAt).toLocaleString()}`
  }
  return `Started ${new Date(props.run.startedAt).toLocaleString()}`
})
</script>

<style scoped>
.workflow-run-panel {
  min-height: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.workflow-run-content {
  overflow-y: auto;
  min-height: 0;
  flex: 1 1 auto;
}
</style>
